import { useState } from "react";
import { EMOTIONS, TOOL_TYPES, WALTER_STATES, type Point, type ToolType, type WalterSnapshot } from "../core/types";
import { walterController } from "../core/walterController";

interface DeveloperPanelProps {
  snapshot: WalterSnapshot;
  fps: number;
  touch: Point;
  safeArea: boolean;
  onSafeArea: (enabled: boolean) => void;
}

export function DeveloperPanel({ snapshot, fps, touch, safeArea, onSafeArea }: DeveloperPanelProps) {
  const [open, setOpen] = useState(false);
  const [tool, setTool] = useState<ToolType>("memory");
  const [testSignal, setTestSignal] = useState("");

  const fullscreen = () => document.documentElement.requestFullscreen?.().catch(() => undefined);

  return (
    <aside className={`developer-panel ${open ? "is-open" : ""}`} data-testid="developer-panel">
      <button className="developer-toggle" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span className="toggle-mark">⌘</span>
        <span>{open ? "Close console" : "Walter console"}</span>
      </button>
      {open && (
        <div className="developer-content">
          <header>
            <span>NERVOUS SYSTEM</span>
            <strong>{snapshot.state}</strong>
          </header>
          <div className="diagnostics">
            <span>{fps || "—"} FPS</span>
            <span>SEQ {snapshot.seq}</span>
            <span>{snapshot.connection.state}</span>
          </div>
          <div className="diagnostics secondary-diagnostics">
            <span>X {touch.x.toFixed(2)}</span>
            <span>Y {touch.y.toFixed(2)}</span>
            <span>{snapshot.phase}</span>
          </div>
          <label className="control-label">FUNCTIONAL SIGNAL</label>
          <form className="console-signal" onSubmit={(event) => { event.preventDefault(); walterController.submitText(testSignal); setTestSignal(""); }}>
            <input value={testSignal} onChange={(event) => setTestSignal(event.target.value)} placeholder="Ask time or remember…" />
            <button type="submit">Run</button>
          </form>
          <label className="control-label">STATE</label>
          <div className="control-grid state-controls">
            {WALTER_STATES.map((state) => (
              <button key={state} className={snapshot.state === state ? "is-selected" : ""} onClick={() => walterController.setWalterState(state)}>
                {state}
              </button>
            ))}
          </div>
          <label className="control-label" htmlFor="audio-level">AUDIO LEVEL · {snapshot.audioLevel.toFixed(2)}</label>
          <input id="audio-level" type="range" min="0" max="1" step="0.01" value={snapshot.audioLevel} onChange={(event) => walterController.setAudioLevel(Number(event.target.value))} />
          <label className="control-label">GAZE</label>
          <div className="range-pair">
            <input aria-label="Gaze X" type="range" min="-1" max="1" step="0.01" value={snapshot.gaze.x} onChange={(event) => walterController.setGaze(Number(event.target.value), snapshot.gaze.y)} />
            <input aria-label="Gaze Y" type="range" min="-1" max="1" step="0.01" value={snapshot.gaze.y} onChange={(event) => walterController.setGaze(snapshot.gaze.x, Number(event.target.value))} />
          </div>
          <label className="control-label">EMOTION</label>
          <select value={snapshot.emotion} onChange={(event) => walterController.setEmotion(event.target.value as (typeof EMOTIONS)[number])}>
            {EMOTIONS.map((emotion) => <option key={emotion}>{emotion}</option>)}
          </select>
          <label className="control-label">TOOL TRANSFER</label>
          <div className="tool-row">
            <select value={tool} onChange={(event) => setTool(event.target.value as ToolType)}>
              {TOOL_TYPES.map((tool) => <option key={tool}>{tool}</option>)}
            </select>
            <button onClick={() => walterController.triggerToolCall(tool)}>Send</button>
            <button onClick={() => walterController.triggerToolReturn(tool)}>Return</button>
          </div>
          <label className="control-label">DISPLAY LINKS</label>
          <div className="device-controls">
            {(["main", "third-eye", "heart"] as const).map((device) => (
              <button key={device} className={snapshot.devices[device] === "online" ? "is-selected" : ""} onClick={() => walterController.setDevice(device, snapshot.devices[device] === "online" ? "offline" : "online")}>{device}</button>
            ))}
          </div>
          <div className="system-actions">
            <button onClick={walterController.wake}>Wake</button>
            <button onClick={walterController.sleep}>Sleep</button>
            <button onClick={() => onSafeArea(!safeArea)}>{safeArea ? "Hide safe area" : "Show safe area"}</button>
            <button onClick={fullscreen}>Fullscreen</button>
          </div>
        </div>
      )}
    </aside>
  );
}
